import {
  Grid,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  Autocomplete,
  Tooltip,
} from "@mui/material";
import { useState, useEffect } from "react";
import AssignmentIcon from "@mui/icons-material/Assignment";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { useTheme } from "@mui/material/styles";

import { getAttachmentTypes } from "../../services/AttachmentTypeService";
import {
  uploadNotificationCertificateOnline,
  previewNotificationCertificateOnlineLawyers,
} from "../../services/NotificationCertificateService";

import DynamicExcelHelpModal from "../General/DynamicExcelHelpModal";
import CardHeader from "../General/CardHeader";
import GlassCard from "../General/GlassCard";
import FileUploadBox from "../General/FileUploadBox";
import { useToast } from "../../context/ToastContext";
import LawyerSessionRequiredDialog from "../General/Dialog/LawyerSessionRequiredDialog";
import useLawyerSessionQueue from "../../hooks/useLawyerSessionQueue";

export default function UploadNotificationCertificateOnline() {
  const { showToast } = useToast();
  const theme = useTheme();
  const sessionQueue = useLawyerSessionQueue();

  const [loading, setLoading] = useState(false);

  const [types, setTypes] = useState([]);
  const [selectedType, setSelectedType] = useState(null);
  const [selectedExcel, setSelectedExcel] = useState(null);

  const [openHelp, setOpenHelp] = useState(false);

  const fetchTypes = async () => {
    try {
      const res = await getAttachmentTypes();
      const data = res?.data?.data ?? [];

      const notificationCertificateTypes = data.filter(
        (t) => t.use_for_notification_certificate === true,
      );

      setTypes(notificationCertificateTypes);
    } catch (error) {
      setTypes([]);
      showToast("Error al cargar los tipos de escritos para cédulas.", "error")
    }
  };

  useEffect(() => {
    fetchTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFileSelect = (file) => {
    if (!file) return;

    const ext = file.name.split(".").pop().toLowerCase();
    if (!["xls", "xlsx"].includes(ext)) {
      showToast("El archivo Excel debe ser .xls o .xlsx", "info")
      setSelectedExcel(null);
      return;
    }

    setSelectedExcel(file);  };

  const hasTypesLoaded = types.length > 0;
  const canSubmit =
    !loading &&
    !sessionQueue.isPending &&
    hasTypesLoaded &&
    selectedType &&
    selectedExcel;

  const buildFormData = () => {
    const formData = new FormData();
    formData.append("type_id", selectedType.id);
    formData.append("file", selectedExcel);
    return formData;
  };

  const submitFile = async () => {
    setLoading(true);
    try {
      const res = await uploadNotificationCertificateOnline(buildFormData());

      showToast(
        res.data?.message ||
          "Cédulas enviadas a la cola de subida correctamente.",
        "success"
      );

      setSelectedType(null);
      setSelectedExcel(null);
    } catch (error) {
      console.error(error);
      showToast(
        error.response?.data?.message ||
          "Error inesperado al procesar la solicitud.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedType) {
      showToast("Debe seleccionar un tipo de escrito.", "info");
      return;
    }

    if (!selectedExcel) {
      showToast("Debe seleccionar un Excel con los códigos.", "info");
      return;
    }

    try {
      await sessionQueue.checkAndRun(
        () => previewNotificationCertificateOnlineLawyers(buildFormData()),
        submitFile
      );
    } catch (error) {
      console.error(error);
      showToast(
        error.response?.data?.message ||
          "Error inesperado al procesar la solicitud.",
        "error"
      );
    }
  };

  return (
    <Grid container justifyContent="center" sx={{ mt: 4 }}>
      <Grid size={{ xs: 12, sm: 12, md: 12, lg: 10 }}>

        <GlassCard>
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Tooltip title="Ver requisitos del Excel" arrow>
              <HelpOutlineIcon
                sx={{
                  cursor: "pointer",
                  color: theme.palette.primary.main,
                  "&:hover": { color: theme.palette.primary.dark },
                }}
                onClick={() => setOpenHelp(true)}
              />
            </Tooltip>
          </Box>

          <CardHeader
            icon={<AssignmentIcon />}
            title="Subida de Cédulas a SAC"
            description="Seleccioná el tipo de escrito y cargá el Excel con los códigos de cedulas. El sistema generará los escritos y los enviará automáticamente al SAC."
          />

          <Box component="form" onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {!hasTypesLoaded && (
                <Grid size={{ xs: 12 }}>
                  <Alert severity="warning" sx={{ borderRadius: 2 }}>
                    No hay tipos de escritos habilitados para cédulas en el
                    sistema.
                  </Alert>
                </Grid>
              )}

              <Grid size={{ xs: 12 }}>
                <Autocomplete
                  options={types}
                  getOptionLabel={(option) =>
                    option.display_name || option.name || ""
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Tipo de Escrito SAC"
                      fullWidth
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    />
                  )}
                  value={selectedType}
                  onChange={(_, newValue) => setSelectedType(newValue)}
                  disabled={!hasTypesLoaded || loading}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <FileUploadBox
                  selectedFile={selectedExcel}
                  onFileSelect={handleFileSelect}
                  accept=".xls,.xlsx"
                  placeholder="Seleccionar archivo Excel"
                  helperText="O arrastrá y soltá el archivo aquí (.xls, .xlsx)"
                  disabled={!hasTypesLoaded || loading} // Prop opcional si decides agregarla al componente base
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  fullWidth
                  disabled={!canSubmit}
                  sx={{
                    borderRadius: 2.5,
                    fontWeight: 900,
                    textTransform: "none",
                    fontSize: 16,
                  }}
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    "Iniciar carga en SAC"
                  )}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </GlassCard>
      </Grid>

      <DynamicExcelHelpModal
        open={openHelp}
        onClose={() => setOpenHelp(false)}
        title="Requisitos del archivo Excel"
        description="El archivo debe ser formato .xls o .xlsx y contener los códigos de las cédulas finalizadas."
        columns={["Código"]}
        example={["S1/12345678"]}
      />

      <LawyerSessionRequiredDialog {...sessionQueue.dialogProps} />
    </Grid>
  );
}
